import React, { useCallback, useEffect, useState } from 'react';
import useUrlState from '../../../hooks/useUrlState';
import useAdminInfluencersData from './useAdminInfluencersData';
import useAbortableEffect from '../../../hooks/useAbortableEffect';
import styles from './AdminInfluencers.module.css';
import AdminCreatorBar from './AdminCreatorBar/AdminCreatorBar';
import useNonNullContext from '../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../contexts/OptionsContext';
import range from 'lodash/range';
import Skeleton from '../../../ui/Skeleton/Skeleton';
import useOnScroll from '../../../hooks/useOnScroll';
import { showNotification } from '../../../utils/showNotification';
import SearchInput from '../../../ui/SearchInput/SearchInput';
import CreateAdminCreatorModal from './AdminCreatorModals/CreateAdminCreatorModal';
import Button from '../../../ui/Button/Button';
import EditAdminCreatorModal from './AdminCreatorModals/EditAdminCreatorModal';
import Badge from '../../../ui/Badge/Badge';
import { StylesConfig, ValueType } from 'react-select';
import { GenericDropdownOption } from '../../../utils/utils.types';
import { useSelectPaginatedOptions } from '../../../hooks/useSelectPaginatedOptions';
import {
    GetAdminInfluencersOrderingKeys,
    getAdminManager,
    getAdminManagers,
    getApexCreatorAgencies,
    getApexCreatorAgency,
    OrderingValues,
} from '@round/api';
import { Select } from '@round/ui-kit';

const selectStyles: StylesConfig = {
    control: (provided, state) => ({
        ...provided,
        backgroundColor: 'white',
        border: 'none',
        borderBottom: '1px solid #C2CFE0',
        borderRadius: '0',
        boxShadow: 'none',
        height: '100%',
        '&:hover': {
            boxShadow: 'none !important',
        },
        ...(state.isFocused && {
            boxShadow: 'none !important',
        }),
    }),
    container: (provided) => ({
        ...provided,
        minWidth: '13rem',
        height: '2.5rem',
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '100%',
        padding: '0 1rem',
    }),
    input: (provided) => ({
        ...provided,
        height: '100%',
    }),
    menu: (provided) => ({
        ...provided,
        minWidth: '18rem',
    }),
    menuList: (provided) => ({
        ...provided,
        minWidth: '18rem',
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: '100%',
    }),
    singleValue: (provided) => ({
        ...provided,
        height: '100%',
        lineHeight: '2.25rem',
        maxWidth: '10.75rem',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#90A0B7',
    }),
};

const orderingSelectStyles: StylesConfig = {
    ...selectStyles,
    container: (base) => ({
        ...base,
        minWidth: '10rem',
        height: '2.5rem',
    }),
    menu: (provided) => ({
        ...provided,
        minWidth: '10rem',
    }),
    menuList: (provided) => ({
        ...provided,
        minWidth: '10rem',
    }),
};

type UrlState = {
    search: string;
    manager_id: number | undefined;
    roster_share_agency_id: number | undefined;
    ordering: string | undefined;
};

const orderingOptions: GenericDropdownOption<OrderingValues<GetAdminInfluencersOrderingKeys> | undefined>[] = [
    {
        value: '-booking_count',
        label: 'Most booked',
    },
    {
        value: 'booking_count',
        label: 'Least booked',
    },
    {
        value: undefined,
        label: 'Last added',
    },
];

const AdminInfluencers = () => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [influencerIdToEdit, setInfluencerIdToEdit] = useState<number | null>(null);
    const { currencies } = useNonNullContext(OptionsContext);
    const DEFAULT_CURRENCY = currencies.find((curr) => curr.name === 'USD');
    const [urlState, setUrlState] = useUrlState<UrlState>(
        { search: '' },
        { manager_id: 'number', roster_share_agency_id: 'number' }
    );

    const {
        influencers,
        influencerCosts,
        influencerCount,
        instagramUserImages,
        tiktokUserImages,
        init,
        isInitialized,
        isLoading,
        hasErrorLoadingData,
        loadNextPage,
        hasNextPage,
        reset,
        addInfluencer,
        addCosts,
        updateInfluencer,
        refreshInfluencerCosts,
    } = useAdminInfluencersData({
        page_size: 25,
        search: urlState.search,
        manager_id: urlState.manager_id,
        roster_share_agency_id: urlState.roster_share_agency_id,
        ordering: urlState.ordering as OrderingValues<GetAdminInfluencersOrderingKeys>,
    });

    useAbortableEffect(
        (signal) => {
            if (!isInitialized) {
                init({ signal });
            }
        },
        [init, isInitialized]
    );

    const setSearch = (search: string) => {
        setUrlState({ search });
        reset();
    };

    const [selectedManagerOption, setSelectedManagerOption] =
        useState<ValueType<GenericDropdownOption<number>, false>>(null);

    const { props: managerSelectProps } = useSelectPaginatedOptions<
        GenericDropdownOption<number>,
        typeof getAdminManagers
    >({
        mapToOption: (item) => ({ value: item.id, label: item.username }),
        fetchOptions: getAdminManagers,
    });

    const handleSetSelectedManagerOption = useCallback(
        (option: ValueType<GenericDropdownOption<number>, false>) => {
            setUrlState({ manager_id: option?.value });
            setSelectedManagerOption(option);

            reset();
        },
        [reset, setUrlState]
    );

    const fetchInitialManager = useCallback(async (managerId: number) => {
        try {
            const response = await getAdminManager(managerId);
            if (response.status !== 200) {
                showNotification('Could not load manager', 'error');
                return;
            }
            setSelectedManagerOption({
                value: response.data.id,
                label: response.data.username,
            });
        } catch {
            showNotification('Could not load manager', 'error');
        }
    }, []);

    useEffect(() => {
        if (!urlState.manager_id || selectedManagerOption?.value === urlState.manager_id) {
            return;
        }

        const managerOption = managerSelectProps.options.find((option) => option.value === urlState.manager_id);

        if (!managerOption) {
            fetchInitialManager(urlState.manager_id);
            return;
        }
        setSelectedManagerOption(managerOption);
    }, [fetchInitialManager, managerSelectProps.options, urlState.manager_id, selectedManagerOption]);

    const [selectedCreatorAgencyOption, setSelectedCreatorAgencyOption] =
        useState<ValueType<GenericDropdownOption<number>, false>>(null);

    const { props: creatorAgencySelectProps } = useSelectPaginatedOptions<
        GenericDropdownOption<number>,
        typeof getApexCreatorAgencies
    >({
        fetchOptions: getApexCreatorAgencies,
        mapToOption: (item) => ({ value: item.id, label: item.name }),
    });

    const handleSetSelectedCreatorAgencyOption = useCallback(
        (option: ValueType<GenericDropdownOption<number>, false>) => {
            setUrlState({ roster_share_agency_id: option?.value });
            setSelectedCreatorAgencyOption(option);

            reset();
        },
        [reset, setUrlState]
    );

    const fetchInitialCreatorAgency = useCallback(async (id: number, requestInit: RequestInit) => {
        try {
            const response = await getApexCreatorAgency(id, requestInit);
            if (response.status !== 200) {
                showNotification('Could not load roster share agency', 'error');
                return;
            }
            setSelectedCreatorAgencyOption({
                value: response.data.id,
                label: response.data.name,
            });
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }

            showNotification('Could not load roster share agency', 'error');
        }
    }, []);

    useAbortableEffect(
        (signal) => {
            if (
                !urlState.roster_share_agency_id ||
                selectedCreatorAgencyOption?.value === urlState.roster_share_agency_id
            ) {
                return;
            }

            const agencyOption = creatorAgencySelectProps.options.find(
                (option) => option.value === urlState.roster_share_agency_id
            );

            if (!agencyOption) {
                fetchInitialCreatorAgency(urlState.roster_share_agency_id, { signal });
                return;
            }

            setSelectedCreatorAgencyOption(agencyOption);
        },
        [
            fetchInitialCreatorAgency,
            creatorAgencySelectProps.options,
            urlState.roster_share_agency_id,
            selectedCreatorAgencyOption,
        ]
    );

    const onBottomReached = useOnScroll(
        () => {
            if (!isLoading && !hasErrorLoadingData && hasNextPage) {
                loadNextPage().catch(() => showNotification('Could not load next page', 'error'));
            }
        },
        { threshold: 500 }
    );

    useEffect(() => {
        document.getElementById('root')?.addEventListener('scroll', onBottomReached);
        return () => document.getElementById('root')?.removeEventListener('scroll', onBottomReached);
    }, [onBottomReached]);

    const influencerCountDisplayValue = `${influencerCount} Creator${
        typeof influencerCount === 'number' && influencerCount !== 1 ? 's' : ''
    }`;

    const isListLoading = isLoading || (!isInitialized && !hasErrorLoadingData);

    return (
        <>
            <div className={styles.main}>
                <div className={styles.headerBar}>
                    <div className={styles.filtersContainer}>
                        <SearchInput className={styles.search} value={urlState.search} onChange={setSearch} />

                        <Select
                            {...managerSelectProps}
                            placeholder="Filter by manager..."
                            value={selectedManagerOption}
                            onChange={(option) => {
                                handleSetSelectedManagerOption(option);
                            }}
                            styles={selectStyles}
                            isClearable
                        />

                        <Select
                            {...creatorAgencySelectProps}
                            placeholder="Filter by roster share..."
                            value={selectedCreatorAgencyOption}
                            onChange={(option) => {
                                handleSetSelectedCreatorAgencyOption(option);
                            }}
                            styles={selectStyles}
                            isClearable
                        />

                        {!hasErrorLoadingData && (
                            <Badge className={styles.countDisplayBadge} loading={isListLoading}>
                                {influencerCountDisplayValue}
                            </Badge>
                        )}
                    </div>

                    <div className={styles.headerRight}>
                        <div>
                            <label className={styles.orderingLabel}>Order by</label>
                            <Select
                                styles={orderingSelectStyles}
                                isSearchable={false}
                                placeholder="Order by..."
                                value={orderingOptions.find((o) => o.value === urlState.ordering)}
                                onChange={(o) => {
                                    setUrlState({ ordering: o?.value });
                                    reset();
                                }}
                                options={orderingOptions}
                            />
                        </div>
                        <Button className={styles.addInfluencerButton} onClick={() => setIsCreateModalOpen(true)}>
                            Add New Creator
                        </Button>
                    </div>
                </div>

                <div className={styles.list}>
                    {influencers.map((inf) => {
                        const currencyToUse =
                            currencies.find((curr) => curr.id === inf.preferred_currency) || DEFAULT_CURRENCY;
                        const image =
                            tiktokUserImages.find((image) => image.user_id === inf.tiktok_user_id) ??
                            instagramUserImages.find((image) => image.user_id === inf.instagram_user_id);

                        return (
                            <AdminCreatorBar
                                key={inf.id}
                                influencer={inf}
                                influencerCostData={influencerCosts.filter(
                                    (cost) => cost.influencer === inf.id && cost.currency === currencyToUse?.id
                                )}
                                image={image?.avatar_thumb}
                                currency={currencyToUse}
                                onClickEdit={(id) => {
                                    setInfluencerIdToEdit(id);
                                    setIsEditModalOpen(true);
                                }}
                            />
                        );
                    })}

                    {isListLoading &&
                        range(0, 4).map((i) => (
                            <Skeleton
                                key={i}
                                baseColor="#fbfbfb"
                                highlightColor="#F5F6F8"
                                containerClassName={styles.loadingBarContainer}
                                className={styles.loadingBar}
                            />
                        ))}
                </div>
            </div>

            <CreateAdminCreatorModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onCreated={({ influencer, costs }) => {
                    showNotification('Creator added', 'info');
                    addInfluencer({
                        id: influencer.id,
                        instagram_user_id: influencer.instagram_user_id,
                        tiktok_user_id: influencer.tiktok_user_id,
                        name: influencer.name,
                        tiktok_username: influencer.tiktok_username,
                        instagram_username: influencer.instagram_username,
                        preferred_currency: influencer.preferred_currency,
                        instagram_display_name: influencer.name,
                        tiktok_display_name: influencer.name,
                        youtube_username: influencer.youtube_username,
                        is_available_for_booking: influencer.is_available_for_booking,
                        booking_count: 0,
                    });
                    addCosts(costs);
                }}
            />

            <EditAdminCreatorModal
                influencerId={influencerIdToEdit}
                influencerCosts={influencerCosts.filter((cost) => cost.influencer === influencerIdToEdit)}
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setInfluencerIdToEdit(null);
                }}
                onUpdated={({ influencer, costsChanged }) => {
                    if (influencer) {
                        updateInfluencer({
                            id: influencer.id,
                            instagram_user_id: influencer.instagram_user_id,
                            name: influencer.name,
                            tiktok_display_name: influencer.name,
                            tiktok_username: influencer.tiktok_username,
                            instagram_username: influencer.instagram_username,
                            preferred_currency: influencer.preferred_currency,
                            instagram_display_name: influencer.name,
                            youtube_username: influencer.youtube_username,
                            tiktok_user_id: influencer.tiktok_user_id,
                            is_available_for_booking: influencer.is_available_for_booking,
                        });
                    }

                    if (costsChanged && typeof influencerIdToEdit === 'number') {
                        refreshInfluencerCosts(influencerIdToEdit).catch((e) => showNotification(e.message, 'error'));
                    }
                }}
            />
        </>
    );
};

export default AdminInfluencers;
